<template>
  <div class="ai-overview-card">
    <div class="ai-overview-card__header">
      <div class="ai-overview-card__title-row">
        <span class="ai-overview-card__sparkles material-icons">auto_awesome</span>
        <span class="ai-overview-card__title">Google AI Overview mode</span>
        <span class="ai-overview-card__badge">NEW</span>
      </div>
      
      <div class="ai-overview-card__toggle-stack">
        <label class="ai-overview-card__toggle">
          <span class="ai-overview-card__toggle-label">Use Google AI Overview sources</span>
          <input
            type="checkbox"
            :checked="enabled"
            :disabled="disabled"
            @change="$emit('enabledchange', $event.target.checked)"
          />
          <span class="ai-overview-card__toggle-slider"></span>
        </label>
      </div>
    </div>

    <div class="ai-overview-card__body">
      <div class="ai-overview-card__lead-row">
        <p class="ai-overview-card__description ai-overview-card__description--primary">
          We'll compare your page to the websites Google links to in AI Overviews for this keyword.
        </p>
        <p class="ai-overview-card__toggle-credit" v-if="!isWhiteGloveUser">
          ({{GOOGLE_AI_OVERVIEW_FEATURE_PRICE}} POP credit to run this feature)
        </p>
      </div>

      <div class="ai-overview-card__secondary-row">
        <p class="ai-overview-card__description ai-overview-card__description--secondary">
          This helps you align with what Google is already citing in its AI Overview.
        </p>

        <div class="ai-overview-card__help-row">
          <v-tooltip top color="transparent">
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="ai-overview-card__help"
                v-bind="attrs"
                v-on="on"
              >
                What are AI Overview sources?
                <span class="material-icons">info</span>
              </button>
            </template>
            <div class="white-bg-tooltip small-tooltip">
              AI Overview sources are the webpages Google cites directly inside the AI Overview block for your keyword.
            </div>
          </v-tooltip>
        </div>
      </div>

      <div v-if="disabled && disabledMessage" class="ai-overview-card__warning">
        {{ disabledMessage }}
      </div>

      <template v-if="enabled">
        <div class="ai-overview-card__comparison-header">
          <span class="ai-overview-card__comparison-title">Comparison set</span>
        </div>

        <label class="ai-overview-card__option">
          <input
            type="radio"
            name="google-ai-overview-comparison-mode"
            value="ai_overview_only"
            :checked="comparisonMode === 'ai_overview_only'"
            :disabled="disabled || !enabled"
            @change="$emit('comparisonmodechange', $event.target.value)"
          />
          <div>
            <div class="ai-overview-card__option-title">
              Google AI Overview sources only
            </div>
            <div class="ai-overview-card__option-description">
              Uses the websites linked inside the AI Overview.
            </div>
          </div>
        </label>

        <label class="ai-overview-card__option">
          <input
            type="radio"
            name="google-ai-overview-comparison-mode"
            value="ai_overview_mixed"
            :checked="comparisonMode === 'ai_overview_mixed'"
            :disabled="disabled || !enabled"
            @change="$emit('comparisonmodechange', $event.target.value)"
          />
          <div>
            <div class="ai-overview-card__option-title">
              Google AI Overview sources and Google Search results
            </div>
            <div class="ai-overview-card__option-description">
              Starts with AI Overview sources, then adds top Search results (duplicates removed).
            </div>
          </div>
        </label>

        <div class="ai-overview-card__footer">
          AI Overviews and Search results can differ-this lets you cover one or both.
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { GOOGLE_AI_OVERVIEW_FEATURE_PRICE } from '@/components/plans_constants.js'
import { getPureServicePkgName } from '@/utils'
import { whiteGlovePlans } from "@/components/plans_constants"

export default {
  props: {
    enabled: {
      type: Boolean,
      default: false,
    },
    comparisonMode: {
      type: String,
      default: 'ai_overview_only',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disabledMessage: {
      type: String,
      default: '',
    },
  },
  computed: {
    GOOGLE_AI_OVERVIEW_FEATURE_PRICE() {
      return GOOGLE_AI_OVERVIEW_FEATURE_PRICE
    },
    isWhiteGloveUser () {
      if (!this.$store.state.user) return false;

      const accountType = this.$store.state.selectedSubAccountRelation
          ?this.$store.state.selectedSubAccountRelation.ownerAccountType
          :this.$store.state.user.accountType

      if (whiteGlovePlans.includes(getPureServicePkgName(accountType))) {
        return true;
      }
      return false;
    },
  }
}
</script>

<style scoped lang="scss">
.ai-overview-card {
  margin-top: 20px;
  padding: 20px 18px 16px;
  border: 1px solid var(--v-grayColor-base);
  border-radius: 6px;
  background: var(--v-cardBg-base);
}

.ai-overview-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ai-overview-card__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--v-mainColor-base);
  font-weight: 700;
}

.ai-overview-card__sparkles {
  font-size: 18px;
  color: var(--v-profileAnchorColor-base);
}

.ai-overview-card__title {
  font-size: 1.05rem;
}

.ai-overview-card__badge {
  padding: 4px 10px;
  border: 1px solid var(--v-profileAnchorColor-base);
  border-radius: 6px;
  color: var(--v-profileAnchorColor-base);
  font-size: 0.85rem;
  font-weight: 600;
  background-color: #FFEDDD;
}

.ai-overview-card__toggle-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.ai-overview-card__toggle-credit {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.35;
  color: var(--v-gray2Color-base);
  text-align: right;
  max-width: 240px;
}

.ai-overview-card__toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--v-mainColor-base);
  font-size: 0.95rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.ai-overview-card__toggle input {
  display: none;
}

.ai-overview-card__toggle-slider {
  position: relative;
  width: 40px;
  height: 22px;
  border-radius: 999px;
  background: var(--v-grayColor-base);
  transition: background 0.2s ease;
}

.ai-overview-card__toggle-slider::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s ease;
}

.ai-overview-card__toggle input:checked + .ai-overview-card__toggle-slider {
  background: rgb(37, 169, 122);
}

.ai-overview-card__toggle input:checked + .ai-overview-card__toggle-slider::after {
  transform: translateX(18px);
}

.ai-overview-card__toggle input:disabled + .ai-overview-card__toggle-slider {
  opacity: 0.6;
}

.ai-overview-card__body {
  margin-top: 14px;
  color: var(--v-mainColor-base);
}

.ai-overview-card__description {
  margin: 0;
}

.ai-overview-card__description--primary {
  color: var(--v-mainColor-base);
}

.ai-overview-card__description--secondary {
  color: var(--v-gray2Color-base);
}

.ai-overview-card__lead-row,
.ai-overview-card__secondary-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.ai-overview-card__secondary-row {
  margin-top: 8px;
}

.ai-overview-card__warning {
  margin-top: 8px;
  margin-bottom: 8px;
  color: var(--v-profileAnchorColor-base);
  font-size: 0.92rem;
}

.ai-overview-card__comparison-header {
  margin-top: 16px;
  margin-bottom: 10px;
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  align-items: center;
}

.ai-overview-card__comparison-title {
  font-weight: 700;
}

.ai-overview-card__help-row {
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}

.ai-overview-card__help {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--v-gray2Color-base);
  cursor: pointer;
  font-size: 0.92rem;
}

.ai-overview-card__help .material-icons {
  font-size: 16px;
}

.ai-overview-card__option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
}

.ai-overview-card__option-title {
  font-weight: 500;
}

.ai-overview-card__option-description {
  margin-top: 4px;
  color: var(--v-gray2Color-base);
}

.ai-overview-card__footer {
  color: var(--v-gray2Color-base);
}

@media (max-width: 900px) {
  .ai-overview-card__lead-row,
  .ai-overview-card__secondary-row,
  .ai-overview-card__comparison-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .ai-overview-card__help-row {
    justify-content: flex-start;
  }

  .ai-overview-card__toggle-stack {
    align-items: flex-start;
  }

  .ai-overview-card__toggle-credit {
    text-align: left;
    max-width: none;
  }

  .ai-overview-card__toggle {
    white-space: normal;
  }
}
</style>
